/**
 * The full set of language codes the picker offers is *enumerated from the
 * browser* — `Intl.supportedValuesOf` does not cover languages, but
 * `Intl.DisplayNames({ fallback: 'none' })` returns `undefined` for codes the
 * runtime does not recognize, so iterating the alpha-2 grid (a–z × a–z) and
 * keeping the resolvable ones gives us "every language the browser knows"
 * without a hand-maintained list.
 */
const hasIntlDisplayNames = typeof Intl !== 'undefined' && typeof Intl.DisplayNames === 'function';

/**
 * `Intl.DisplayNames` instance whose `.of()` returns `undefined` for unknown
 * codes (the default `fallback: 'code'` would echo the input back, defeating
 * the filter). Used both for label resolution and code enumeration.
 */
const englishDisplayNames = hasIntlDisplayNames
    ? new Intl.DisplayNames(['en'], { type: 'language', fallback: 'none' })
    : null;

/**
 * Cache of in-language `Intl.DisplayNames` instances keyed by BCP-47 code.
 * Spinning up one of these is non-trivial.
 *
 * @type {Object<string, Intl.DisplayNames|null>}
 */
const nativeDisplayNamesByLang = {};

function getNativeDisplayNamesFor(lang) {
    if (!hasIntlDisplayNames) return null;
    if (!(lang in nativeDisplayNamesByLang)) {
        try {
            nativeDisplayNamesByLang[lang] = new Intl.DisplayNames([lang], { type: 'language', fallback: 'none' });
        } catch {
            nativeDisplayNamesByLang[lang] = null;
        }
    }
    return nativeDisplayNamesByLang[lang];
}

/**
 * @typedef {Object} ScreenerTranslationLangBlock
 * @property {Object<string, ScreenerTranslationQuestion>} [questions] keyed by element.id
 * @property {Object<string, string>} [ui] optional per-language UI chrome overrides
 */

/**
 * @typedef {Object} ScreenerTranslationQuestion
 * @property {string} [label]
 * @property {string} [description]
 * @property {Object<string, string>} [options] keyed by option.key (or id/value when key is absent)
 */

/**
 * A stable per-option shape for translation lookup. See {@link getOptionId}.
 *
 * @typedef {Object} FormOption
 * @property {string} [key]     preferred stable id (survives label edits)
 * @property {string} [id]
 * @property {string} [value]
 * @property {string} [text]    the display text (source language)
 */

/**
 * The translatable surface of a form element — the fields the overlay replaces
 * and the Form Builder mirrors as source hints while editing a translation.
 * Every field is optional; only those present on a given element type apply.
 *
 * @typedef {Object} TranslationSourceElement
 * @property {string} [id]              element id (overlay/lookup key)
 * @property {string} [label]          question label
 * @property {string} [content]        Header/Paragraph/Label body text
 * @property {string} [description]
 * @property {string} [min_label]      RangeElement lower bookend
 * @property {string} [max_label]      RangeElement upper bookend
 * @property {FormOption[]} [options]  answer options (source language)
 */

/**
 * @typedef {Object} ScreenerTranslation
 * @property {string} [default_lang]            optional source language declaration (e.g. `fr`); unset means the primary content is language-agnostic
 * @property {Object<string, ScreenerTranslationLangBlock>} [lang]  per-language overlay blocks
 */

/**
 * The primary/source content (`form` JSON) is language-agnostic — internally it
 * is the `PRIMARY_LANG_VALUE` ("__primary__") layer, never a fixed language.
 * `screener_translation.default_lang` is *optional* metadata an author may set
 * to declare which language that primary content is written in (e.g. `'fr'`);
 * when unset we make no assumption about it. Returns the configured code or
 * `null` when the author has not declared one.
 *
 * @param {ScreenerTranslation} [translation]
 * @returns {string|null}
 */
export const getDefaultLang = translation => (translation && translation.default_lang) || null;

/**
 * ISO 15924 script codes written right-to-left.
 *
 * Direction is a property of the *script*, not the language, so this is the
 * closed set the decision actually turns on — unlike a language list, it does
 * not grow when the picker gains a locale.
 *
 * Limited to scripts a respondent could plausibly be served; the long tail of
 * historic RTL scripts (Avestan, Palmyrene, Old Turkic, …) is omitted
 * deliberately. `Armi` is here because Aramaic (`arc`) maximizes to it.
 */
const RTL_SCRIPTS = ['Arab', 'Aran', 'Armi', 'Hebr', 'Thaa', 'Syrc', 'Samr', 'Mand', 'Nkoo', 'Adlm', 'Rohg', 'Yezi'];

/**
 * Right-to-left languages, consulted only when the runtime cannot resolve a
 * script. Carries both the alpha-2 codes the picker offers and the alpha-3
 * forms a hand-written `default_lang` may use.
 */
const RTL_LANGUAGES_FALLBACK = [
    'ar',
    'ara',
    'arc',
    'azb',
    'ckb',
    'div',
    'dv',
    'fa',
    'fas',
    'he',
    'heb',
    'kas',
    'ks',
    'nqo',
    'pnb',
    'prs',
    'ps',
    'pus',
    'sd',
    'sdh',
    'snd',
    'syr',
    'ug',
    'uig',
    'ur',
    'urd',
    'yi',
    'yid'
];

/**
 * Base text direction for a BCP-47 language code — `'ltr'` or `'rtl'`.
 *
 * Resolves the language's script via `Intl.Locale#maximize()` and checks it
 * against {@link RTL_SCRIPTS}. Maximizing applies the CLDR likely-subtags
 * expansion (`ar` → `Arab`, `dv` → `Thaa`) while honouring an explicit script
 * subtag (`az` → `Latn`, `az-Arab` → `Arab`).
 *
 * A region subtag *can* change the answer, and should: CLDR reads region when
 * inferring script, so `az-IR` → `Arab`, `pa-PK` → `Arab` (Shahmukhi) and
 * `uz-AF` → `Arab`, while `az`, `pa-IN` and `uz` stay Latin or Gurmukhi. Those
 * communities really do write right-to-left, so honouring the region is the
 * whole benefit of resolving a script instead of mapping a language list —
 * a bare `az` is the case we get *wrong*, not `az-IR`. Where the script does
 * not vary by region, the region is inert (`ar-QA` matches `ar`).
 *
 * Deliberately **not** `Intl.Locale`'s `textInfo.direction`, which looks like
 * the purpose-built API but contradicts the script reported by the same
 * object — on Node 18's ICU it calls both `dv` and `az-Arab` left-to-right.
 * That varies by engine, so using it would let one screener render differently
 * on two respondents' devices.
 *
 * When the runtime cannot help — no `Intl.Locale`, no `maximize()`, or a tag
 * it refuses to parse — {@link RTL_LANGUAGES_FALLBACK} is matched against the
 * primary subtag. Matching the prefix is what keeps a lightly malformed tag
 * (`'ar-'`, which `Intl.Locale` rejects outright) rendering as Arabic rather
 * than silently flipping to `'ltr'`. Unknown, empty and non-string input all
 * yield `'ltr'`.
 *
 * @param {string|null|undefined} code
 * @returns {'ltr'|'rtl'}
 */
export const getLanguageDirection = code => {
    if (!code || typeof code !== 'string') return 'ltr';

    if (typeof Intl !== 'undefined' && typeof Intl.Locale === 'function') {
        try {
            const locale = new Intl.Locale(code);
            const script = typeof locale.maximize === 'function' ? locale.maximize().script : locale.script;
            if (script) return RTL_SCRIPTS.includes(script) ? 'rtl' : 'ltr';
        } catch {
            // Malformed code, or a runtime without `maximize()` — fall through.
        }
    }

    return RTL_LANGUAGES_FALLBACK.includes(code.toLowerCase().split('-')[0]) ? 'rtl' : 'ltr';
};

/**
 * Base text direction for a screener, or `null` when no language is known and
 * the document direction should be left alone.
 *
 * Resolution order mirrors what the respondent is actually looking at:
 *   1. `language` — the code the API resolved for this request. Present only
 *      on multi-language screeners, and already reflects `?lang=` and
 *      `Accept-Language` negotiation, so we never re-derive that here.
 *   2. `screener_translation.default_lang` — the author's declaration of what
 *      the primary content is written in. This is the single-language case:
 *      an Arabic-only screener has no `language`, but always carries this.
 *   3. Neither — an author who never declared a language. Return `null` rather
 *      than guessing `'ltr'`, so the caller omits `dir` entirely.
 *
 * @param {string|null} language resolved language code from the API response
 * @param {ScreenerTranslation} [translation]
 * @returns {'ltr'|'rtl'|null}
 */
export const resolveScreenerDirection = (language, translation) => {
    const code = language || getDefaultLang(translation);
    return code ? getLanguageDirection(code) : null;
};

/**
 * Resolve a BCP-47 language code to display metadata via the browser's
 * `Intl.DisplayNames`. `label` is the English name (e.g. `'Spanish'`),
 * `nativeLabel` is the name in that language (e.g. `'Español'`). Falls back
 * to the raw code when the runtime cannot resolve a name — keeps the UI
 * robust against legacy data carrying unexpected codes and against runtimes
 * without `Intl.DisplayNames`.
 *
 * @param {string} code
 * @returns {{ code: string, label: string, nativeLabel: string }}
 */
export const findLanguageMeta = code => {
    const label = (englishDisplayNames && safeOf(englishDisplayNames, code)) || code;
    const native = getNativeDisplayNamesFor(code);
    const nativeLabel = (native && safeOf(native, code)) || label;
    return { code, label, nativeLabel };
};

/**
 * Whether the runtime canonicalizes deprecated ISO-639 codes (ES2018). Present
 * wherever `Intl.DisplayNames` is, but guarded independently for robustness.
 */
const hasCanonicalLocales = typeof Intl !== 'undefined' && typeof Intl.getCanonicalLocales === 'function';

/**
 * A code is "canonical" when the runtime maps it to itself. Deprecated ISO-639
 * aliases resolve to their modern form (`iw`→`he`, `in`→`id`, `ji`→`yi`,
 * `jw`→`jv`, `mo`→`ro`), so filtering to canonical codes drops the aliases and
 * keeps the preferred code. When `getCanonicalLocales` is unavailable we keep
 * everything and rely on the label-dedupe pass below.
 *
 * @param {string} code
 * @returns {boolean}
 */
function isCanonicalCode(code) {
    if (!hasCanonicalLocales) return true;
    try {
        return Intl.getCanonicalLocales(code)[0] === code;
    } catch {
        return true;
    }
}

/**
 * Enumerate every alpha-2 language code the browser's CLDR data recognizes,
 * resolve display metadata via `findLanguageMeta`, and alphabetize by the
 * English `label`. Duplicates that would confuse the picker are removed:
 * deprecated ISO-639 aliases are dropped in favor of their canonical code, and
 * any residual codes sharing one English label (e.g. `ak`/`tw` both → "Akan",
 * both canonical) are collapsed to the first. Memoized — locale-stable for the
 * session.
 *
 * @returns {Array<{ code: string, label: string, nativeLabel: string }>}
 */
let _cachedSupportedLanguages = null;
export const listSupportedLanguages = () => {
    if (_cachedSupportedLanguages) return _cachedSupportedLanguages;
    if (!englishDisplayNames) {
        _cachedSupportedLanguages = [];
        return _cachedSupportedLanguages;
    }
    const codes = [];
    for (let a = 97; a <= 122; a += 1) {
        for (let b = 97; b <= 122; b += 1) {
            const code = String.fromCharCode(a, b);
            if (safeOf(englishDisplayNames, code) && isCanonicalCode(code)) codes.push(code);
        }
    }
    const seenLabels = new Set();
    _cachedSupportedLanguages = codes
        .map(findLanguageMeta)
        .sort((a, b) => a.label.localeCompare(b.label))
        .filter(l => {
            if (seenLabels.has(l.label)) return false;
            seenLabels.add(l.label);
            return true;
        });
    return _cachedSupportedLanguages;
};

function safeOf(displayNames, code) {
    try {
        return displayNames.of(code);
    } catch {
        return null;
    }
}

/**
 * List language codes configured on a screener_translation object, default
 * first. The default language is always present even when no overlay block
 * exists for it (matches the backend's `getAvailableLanguages`).
 *
 * @param {ScreenerTranslation} translation
 * @param {string} defaultLang  resolved default language (caller controls the `'en'` fallback)
 * @returns {string[]}
 */
export const getConfiguredLanguageCodes = (translation, defaultLang) => {
    const extra = Object.keys(translation?.lang || {}).filter(c => c !== defaultLang);
    return [defaultLang, ...extra];
};

/**
 * Flatten a form (array of pages of elements) into a lookup keyed by element
 * `id`. Used while editing a translation to surface each element's primary
 * (source) values next to the translation inputs — the overlaid form the editor
 * renders has those source strings blanked, so the lookup preserves them.
 *
 * @param {Array<Array<TranslationSourceElement>>} form
 * @returns {Object<string, TranslationSourceElement>} element id → primary element
 */
export const indexFormById = form => {
    const byId = {};
    if (!Array.isArray(form)) return byId;
    form.forEach(page => {
        if (!Array.isArray(page)) return;
        page.forEach(element => {
            if (element && element.id) byId[element.id] = element;
        });
    });
    return byId;
};

/**
 * Primary/source values keyed by element id, for surfacing next to the
 * translation inputs while editing a translation. Returns `null` on the
 * Primary layer (the editor shows no hints there) and when no form is
 * available; otherwise the `indexFormById` lookup, since `getEffectiveForm`
 * blanks these source strings out of the overlaid form the builder renders.
 *
 * Single source of truth for the "hints only when overlaying a translation"
 * rule shared by the study-screener and panel-signup editors.
 *
 * @param {Array<Array<TranslationSourceElement>>} form  primary (source) form
 * @param {string} lang  currently-edited language
 * @param {string} primaryLangValue  sentinel value the picker uses for "Primary"
 * @returns {Object<string, TranslationSourceElement>|null}
 */
export const getTranslationSourceById = (form, lang, primaryLangValue) => {
    if (!form || lang === primaryLangValue) return null;
    return indexFormById(form);
};

/**
 * Stable per-option identifier used for translation lookup. `option.key` is
 * the only field that survives label edits — `option.value` is recomputed
 * from `option.text` on every edit by dynamic-option-list.jsx, and `option.id`
 * is not always present. Keep this single source of truth.
 *
 * @param {Object} option
 * @returns {string|undefined}
 */
export function getOptionId(option) {
    if (!option) return undefined;
    return option.key || option.id || option.value;
}

/**
 * Build a copy of `form` with every translatable string replaced by its
 * translation in `lang`. Strings without a translation are blanked so the
 * admin sees an empty input to fill in (and ReactFormBuilder won't echo the
 * Primary text into the translation save payload).
 *
 * Mirrors the backend overlay at `app/Managers/ScreenerTranslationManager::applyLanguageOverlay`,
 * but writes to the fields ReactFormBuilder actually reads:
 *   - element.label  (questions)        ← translation block .label
 *   - element.content (Header/Paragraph) ← translation block .label
 *   - element.description               ← translation block .description
 *   - option.text                       ← translation block .options[id]
 *
 * @param {Array<Array<Object>>} form
 * @param {ScreenerTranslation} translation
 * @param {string} lang
 * @returns {Array<Array<Object>>}
 */
/**
 * Bookend labels on slider/range elements (`RangeElement`). Kept as a single
 * list so the overlay logic stays DRY — adding a new scalar label means
 * editing this array (and its API counterpart `EXTRA_LABEL_FIELDS`).
 */
const EXTRA_LABEL_FIELDS = ['min_label', 'max_label'];

export function buildOverlaidForm(form, translation, lang) {
    if (!Array.isArray(form)) return form;
    const langBlock = translation?.lang?.[lang]?.questions || {};

    return form.map(page => {
        if (!Array.isArray(page)) return page;
        return page.map(element => {
            if (!element || !element.id) return element;
            const block = langBlock[element.id] || {};
            const next = { ...element };
            const isHeaderLike =
                element.element === 'Header' || element.element === 'Paragraph' || element.element === 'Label';

            if (isHeaderLike) {
                next.content = block.label || '';
            } else {
                next.label = block.label || '';
            }
            if (element.description !== undefined) {
                next.description = block.description || '';
            }
            EXTRA_LABEL_FIELDS.forEach(field => {
                if (element[field] !== undefined) {
                    next[field] = block[field] || '';
                }
            });

            if (Array.isArray(element.options) && element.options.length > 0) {
                const optTranslations = block.options || {};
                next.options = element.options.map(option => {
                    const optionId = getOptionId(option);
                    if (!optionId) return option;
                    return { ...option, text: optTranslations[optionId] || '' };
                });
            }
            return next;
        });
    });
}

/**
 * Effective form to render in the Form Builder for a given editing language.
 * Returns the raw `form` for Primary; otherwise an overlay-applied copy.
 *
 * @param {Array<Array<Object>>} form
 * @param {ScreenerTranslation} translation
 * @param {string} lang
 * @param {string} primaryLangValue  sentinel value the picker uses for "Primary"
 * @returns {Array<Array<Object>>}
 */
export function getEffectiveForm(form, translation, lang, primaryLangValue) {
    if (!Array.isArray(form)) return [];
    if (!lang || lang === primaryLangValue) return form;
    return buildOverlaidForm(form, translation || {}, lang);
}

/**
 * Push a language switch into the ReactFormBuilder store. The save URL must
 * be updated **before** the next render — `getDerivedStateFromProps` in
 * preview.jsx auto-dispatches `updateOrder` with `saveData: true` whenever the
 * `data` prop changes, and that POST lands on whatever URL the store
 * currently holds. We also dispatch the overlay-applied form ourselves with
 * `saveData: false` because the preview renders from the store (not from the
 * `data` prop), so remounting via `key` alone leaves stale form data in view.
 *
 * @param {Object} store         ReactFormBuilder Beedle store
 * @param {string} saveUrl       URL that subsequent form saves should POST to
 * @param {Array<Array<Object>>} nextForm  form to display in the builder
 */
export function applyLanguageChangeToBuilder(store, saveUrl, nextForm) {
    store.dispatch('updateSaveUrl', saveUrl);
    store.dispatch('updateOrder', { elements: nextForm, saveData: false });
}

/**
 * Decide what language should be active after a `screener_translation` update.
 * Snaps back to Primary when the currently-edited language has been removed.
 *
 * @param {string} currentLang
 * @param {ScreenerTranslation} updatedTranslation
 * @param {string} primaryLangValue
 * @returns {string}
 */
export function resolveLanguageAfterUpdate(currentLang, updatedTranslation, primaryLangValue) {
    if (currentLang === primaryLangValue) return currentLang;
    const enabled = Object.keys(updatedTranslation?.lang || {});
    return enabled.includes(currentLang) ? currentLang : primaryLangValue;
}
